import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";

export type CaseItem = {
  brand: string;
  logo: string;
  thumb: string;
  tags: string[];
  title: string;
  desc: string;
  flow?: string;
  bullets: string[];
};

export function CaseMarquee({ cases, readLabel }: { cases: CaseItem[]; readLabel: string }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [paused, setPaused] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const posRef = useRef(cases.length);
  const loop = [...cases, ...cases, ...cases];
  const baseStart = cases.length;
  const N = cases.length;

  const targetLeftFor = (pos: number) => {
    const el = scrollerRef.current;
    const card = cardRefs.current[pos];
    if (!el || !card) return null;
    return card.offsetLeft - (el.clientWidth - card.clientWidth) / 2;
  };

  useEffect(() => {
    const t = targetLeftFor(baseStart);
    if (t == null) return;
    scrollerRef.current!.scrollLeft = t;
    posRef.current = baseStart;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseStart]);

  const rebaseIfNeeded = () => {
    const el = scrollerRef.current;
    if (!el) return;
    let pos = posRef.current;
    if (pos >= 2 * N) pos -= N;
    else if (pos < N) pos += N;
    else return;
    const t = targetLeftFor(pos);
    if (t == null) return;
    el.scrollLeft = t;
    posRef.current = pos;
  };

  const scrollToPos = (pos: number, smooth = true) => {
    const el = scrollerRef.current;
    const t = targetLeftFor(pos);
    if (!el || t == null) return;
    posRef.current = pos;
    setActiveIdx(((pos % N) + N) % N);
    el.scrollTo({ left: t, behavior: smooth ? "smooth" : "auto" });
    if (smooth) {
      window.setTimeout(rebaseIfNeeded, 700);
    } else {
      rebaseIfNeeded();
    }
  };

  const scrollToCard = (i: number, smooth = true) => {
    const idx = ((i % N) + N) % N;
    const cur = posRef.current;
    const curIdx = ((cur % N) + N) % N;
    const delta = (idx - curIdx + N) % N || N;
    const next = i === curIdx ? cur : cur + delta;
    scrollToPos(next, smooth);
  };

  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => {
      scrollToPos(posRef.current + 1);
    }, 6000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, paused, N]);

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="flex items-center justify-between gap-6 md:gap-10 mb-10 w-full">
        {cases.map((c, i) => (
          <button
            key={c.brand + i}
            onClick={() => scrollToCard(i)}
            aria-label={c.brand}
            className={`group flex-1 h-16 rounded-xl border flex items-center justify-center transition-all ${
              activeIdx === i
                ? "border-[hsl(var(--brand))] bg-white/5"
                : "border-white/10 hover:border-white/30 hover:bg-white/[0.03]"
            }`}
          >
            <img
              src={c.logo}
              alt={c.brand}
              className={`h-12 w-auto object-contain transition-opacity ${
                activeIdx === i ? "opacity-90" : "opacity-90 group-hover:opacity-70"
              }`}
            />
          </button>
        ))}
      </div>

      <div className="relative w-full overflow-hidden rounded-2xl">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-[8%] z-10 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[8%] z-10 bg-gradient-to-l from-background to-transparent" />
        <div ref={scrollerRef} className="flex gap-8 overflow-x-auto no-scrollbar" style={{ scrollbarWidth: "none" }}>
          {loop.map((c, i) => (
            <div
              key={i}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className="glass p-8 md:p-10 grid md:grid-cols-2 gap-8 shrink-0"
              style={{ width: "min(85vw, 1080px)" }}
            >
              <div className="aspect-video rounded-xl relative overflow-hidden nebula-border bg-white/5">
                <img src={c.thumb} alt={c.brand} className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col justify-center">
                <div className="flex flex-wrap gap-2 mb-4">
                  {c.tags.map((tg) => (
                    <span key={tg} className="text-xs px-3 py-1 glass rounded-full">
                      {tg}
                    </span>
                  ))}
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-4">{c.title}</h3>
                <p className="text-muted-foreground leading-relaxed mb-6 whitespace-pre-line">{c.desc}</p>
                {c.flow && <p className="text-xs text-muted-foreground mb-6">{c.flow}</p>}
                <ul className="space-y-2 text-sm mb-6">
                  {c.bullets.map((b) => (
                    <li key={b} className="flex gap-3">
                      <span className="brand-text">▲</span> {b}
                    </li>
                  ))}
                </ul>
                {/* <a href="#" className="btn-secondary self-start">
                  {readLabel} <ArrowRight size={14} />
                </a> */}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
